import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AdminNavbar from "../components/AdminNavbar";
import AdminSidebar from "../components/AdminSidebar";
import { useSidebar } from "../components/SidebarContext";
import api from "../services/api";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Button } from 'react-bootstrap';

export default function AdminCompletedTestsPage() {
  const { isSidebarOpen } = useSidebar();
  const navigate = useNavigate();
  const sidebarWidth = isSidebarOpen ? "15.6rem" : "4.4rem";
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const studentsPerPage = 100;

  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTo(0, 0);
    }
  }, [currentPage]);

  // Helper function to check if error is token-related
  const isTokenError = (error) => {
    return error.response && (
      error.response.status === 401 || 
      error.response.status === 403 ||
      (error.response.data && 
       (error.response.data.message?.toLowerCase().includes('token') ||
        error.response.data.message?.toLowerCase().includes('unauthorized') ||
        error.response.data.message?.toLowerCase().includes('expired')))
    );
  };

  useEffect(() => {
    const fetchCompletedTests = async () => {
      try {
        const response = await api.get('/api/results/completed-tests');
        const formattedStudents = response.data.map(student => ({
          id: student.id,
          studentName: student.studentName,
          sectionName: student.sectionName || 'N/A',
          grade: student.grade,
          testNumber: student.testNumber,
          score: student.score,
          attemptedAt: student.attemptedAt,
        }));
        setStudents(formattedStudents);
        setFilteredStudents(formattedStudents);
        setError(null);
        setIsTokenExpired(false);
      } catch (err) {
        console.error(err);
        if (isTokenError(err)) {
          setIsTokenExpired(true);
          setError("Your session has expired. Please login again.");
        } else {
          setError("Failed to fetch test data. Please try again.");
          setIsTokenExpired(false);
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchCompletedTests();
  }, []);

  useEffect(() => {
    const filtered = students.filter(student => {
      const studentName = (student.studentName || '').toLowerCase();
      const sectionName = (student.sectionName || '').toLowerCase();
      const grade = (student.grade || '').toLowerCase();
      const testNumber = (student.testNumber || '').toString().toLowerCase();
      const score = (student.score || '').toString().toLowerCase();
      const attemptedAt = new Date(student.attemptedAt).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).toLowerCase();
      const search = searchQuery.toLowerCase();

      return (
        studentName.includes(search) ||
        sectionName.includes(search) ||
        grade.includes(search) ||
        testNumber.includes(search) ||
        score.includes(search) ||
        attemptedAt.includes(search)
      );
    });
    setFilteredStudents(filtered);
    setCurrentPage(1);
  }, [searchQuery, students]);

  const handleViewResult = (resultId, studentName) => {
    navigate(`/stored-result/${resultId}`, { 
      state: { 
        from: '/all-completed-Tests',
        isAdmin: true,
        userRole: 'admin',
        studentName: studentName
      } 
    });
  };

  const handleRetry = () => {
    if (isTokenExpired) {
      // Redirect to login if token expired
      navigate('/super-admin');
      return;
    }

    // Regular retry for other errors
    setError(null);
    setLoading(true);
    const fetchCompletedTests = async () => {
      try {
        const response = await api.get('/api/results/completed-tests');
        const formattedStudents = response.data.map(student => ({
          id: student.id,
          studentName: student.studentName,
          sectionName: student.sectionName || 'N/A',
          grade: student.grade,
          testNumber: student.testNumber,
          score: student.score,
          attemptedAt: student.attemptedAt,
        }));
        setStudents(formattedStudents);
        setFilteredStudents(formattedStudents);
        setError(null);
        setIsTokenExpired(false);
      } catch (err) {
        console.error(err);
        if (isTokenError(err)) {
          setIsTokenExpired(true);
          setError("Your session has expired. Please login again.");
        } else {
          setError("Failed to fetch test data. Please try again.");
          setIsTokenExpired(false);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchCompletedTests();
  };

  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredStudents.slice(indexOfFirstStudent, indexOfLastStudent);
  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);

  const paginate = (pageNumber) => {
    const maxPage = Math.min(pageNumber, totalPages);
    setCurrentPage(maxPage > 0 ? maxPage : 1);
    setIsDropdownOpen(false);
  };

  const pageRange = Array.from({ length: totalPages }, (_, i) => i + 1);

  const styles = {
    container: {
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      minHeight: "100vh",
      background: "linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)",
      fontFamily: "'Roboto', sans-serif",
    },
    sidebar: {
      width: sidebarWidth,
      borderRadius: '30px',
      height: '100vh',
      flexShrink: 0,
      fontFamily: "'Roboto', sans-serif",
    },
    content: {
      flex: 1,
      padding: '15px',
      margin: '6rem 2rem 2rem 2rem',
      backgroundColor: 'white',
      transition: 'margin-left 0.3s ease',
      minHeight: 'calc(100vh - 8rem)',
      maxHeight: 'calc(100vh - 8rem)',
      borderTopLeftRadius: '0.5rem',
      borderTopRightRadius: '0.5rem',
      borderBottomLeftRadius: '0.5rem',
      borderBottomRightRadius: '0.5rem',
      maxWidth: `calc(100vw - ${sidebarWidth} - 4rem)`,
      width: '100%',
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      fontFamily: "'Roboto', sans-serif",
    },
    headerContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'center',
      marginBottom: '16px',
      width: '100%',
      flexShrink: 0,
      fontFamily: "'Roboto', sans-serif",
    },
    searchFilterGroup: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '8px',
      fontFamily: "'Roboto', sans-serif",
    },
    searchInput: {
      width: '100%',
      maxWidth: '300px',
      padding: '6px 12px',
      borderRadius: '30px',
      border: '1px solid #ddd',
      fontSize: '14px',
      outline: 'none',
      transition: 'border-color 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    tableContainer: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'auto',
      borderRadius: '0.5rem',
      maxHeight: 'calc(100vh - 12rem)',
      minHeight: '300px',
      width: '100%',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: "'Roboto', sans-serif",
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      border: 'none',
      fontSize: '12px',
      fontFamily: "'Roboto', sans-serif",
    },
    headerRow: {
      background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)',
      position: 'sticky',
      top: 0,
      zIndex: 2,
      fontFamily: "'Roboto', sans-serif",
    },
    th: {
      padding: '12px 10px',
      fontWeight: '500',
      textAlign: 'center',
      color: '#6B21A8',
      fontSize: '12px',
      borderBottom: '2px solid #e5e7eb',
      fontFamily: "'Roboto', sans-serif",
    },
    row: {
      borderRadius: '8px',
      fontFamily: "'Roboto', sans-serif",
    },
    td: {
      padding: '10px 12px',
      verticalAlign: 'middle',
      textAlign: 'center',
      color: '#333',
      fontFamily: "'Roboto', sans-serif",
    },
    actionButton: {
      backgroundColor: 'transparent',
      color: '#cc8888',
      border: 'none',
      borderRadius: '2px',
      padding: '2px',
      cursor: 'pointer',
      transition: 'color 0.3s ease, transform 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    loadingContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      color: '#666',
      fontFamily: "'Roboto', sans-serif",
    },
    errorContainer: {
      backgroundColor: isTokenExpired ? '#FEE7EF' : '#F3E8FF',
      color: '#6B21A8',
      padding: '15px',
      borderRadius: '8px',
      margin: '12px auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
      maxWidth: '500px',
      animation: 'slideInFromRight 0.5s ease-out',
      fontFamily: "'Roboto', sans-serif",
      textAlign: 'center',
      gap: '10px',
    },
    retryButton: {
      borderWidth: '2px',
      fontWeight: '500',
      padding: '8px 20px',
      fontSize: '14px',
      borderColor: '#6B21A8',
      color: '#6B21A8',
      backgroundColor: 'transparent',
      borderRadius: '25px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    noDataContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      color: '#666',
      fontFamily: "'Roboto', sans-serif",
    },
    paginationContainer: {
      background: '#ffffff',
      padding: '10px 0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: '40px',
      marginTop: '20px',
      zIndex: 5,
      fontFamily: "'Roboto', sans-serif",
    },
    pageButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3px 13px',
      fontSize: '13px',
      fontWeight: '600',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      cursor: 'pointer',
      borderRadius: '30px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      fontFamily: "'Roboto', sans-serif",
      flexShrink: '0',
      minWidth: '80px',
      textAlign: 'center',
      transition: 'transform 0.2s ease, background 0.3s ease, color 0.3s ease',
    },
    pageButtonPrevious: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3px 13px',
      fontSize: '13px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      cursor: 'pointer',
      borderRadius: '30px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      fontFamily: "'Roboto', sans-serif",
      flexShrink: '0',
      minWidth: '80px',
      textAlign: 'center',
      transition: 'transform 0.2s ease, background 0.3s ease, color 0.3s ease',
      color: currentPage === 1 ? '#aaa' : '#6B21A8',
    },
    pageNumberButton: {
      width: '30px',
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '13px',
      fontWeight: '600',
      color: '#333',
      cursor: 'pointer',
      borderRadius: '50%',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      fontFamily: "'Roboto', sans-serif",
      transition: 'transform 0.2s ease',
    },
    paginationWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontFamily: "'Roboto', sans-serif",
    },
    customDropdown: {
      width: '60px',
      padding: '5px 25px 5px 10px',
      borderRadius: '5px',
      border: '1px solid #ccc',
      fontSize: '14px',
      color: '#1F2937',
      cursor: 'pointer',
      backgroundColor: '#fff',
      appearance: 'none',
      WebkitAppearance: 'none',
      MozAppearance: 'none',
      backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%236B21A8%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000%20.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2010-1.414-1.414z%22%2F%3E%3C%2Fsvg%3E")',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 10px center',
      backgroundSize: '12px',
      transition: 'border-color 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
      position: 'relative',
    },
    customDropdownOptions: {
      position: 'absolute',
      bottom: '100%',
      left: 0,
      width: '60px',
      border: '1px solid #ccc',
      borderRadius: '5px',
      backgroundColor: '#fff',
      zIndex: 1000,
      maxHeight: '200px',
      overflowY: 'auto',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      fontFamily: "'Roboto', sans-serif",
    },
    customOption: {
      padding: '5px 10px',
      cursor: 'pointer',
      fontFamily: "'Roboto', sans-serif",
    },
  };

  return (
    <div style={styles.container}>
      <style>{`
        input:focus {
          border-color: #6B21A8 !important;
        }
        .custom-table {
          border: none !important;
          font-family: 'Roboto', sans-serif !important;
          table-layout: fixed;
          width: 100%;
          min-width: 100%;
        }
        .custom-table th,
        .custom-table td {
          border: none !important;
          font-family: 'Roboto', sans-serif !important;
        }
        .custom-table tbody tr:nth-child(odd) {
          background-color: #FFFFFF;
        }
        .custom-table tbody tr:nth-child(even) {
          background: linear-gradient(to right, #FAFCFF, #FBF6FF, #FFF8FB);
        }
        .custom-table tbody tr:hover {
          background: linear-gradient(to right, #F0F5FF, #F5EDFF, #FFF0F5) !important;
        }
        .action-button:hover {
          color: #b36666 !important;
          transform: scale(1.1);
          font-family: 'Roboto', sans-serif !important;
        }
        .table-container::-webkit-scrollbar {
          width: 12px;
          height: 12px;
        }
        .table-container::-webkit-scrollbar-track {
          background: #f8f9fa;
          border-radius: 6px;
        }
        .table-container::-webkit-scrollbar-thumb {
          background: #6c757d;
          border-radius: 6px;
          border: 2px solid #f8f9fa;
        }
        .table-container::-webkit-scrollbar-thumb:hover {
          background: #495057;
        }
        .table-container::-webkit-scrollbar-corner {
          background: #f8f9fa;
        }
        .table-container {
          scrollbar-width: thin;
          scrollbar-color: #6c757d #f8f9fa;
          font-family: 'Roboto', sans-serif !important;
          position: relative;
          overflow-y: auto;
          overflow-x: hidden !important;
          display: flex;
          flex-direction: column;
        }
        @keyframes slideInFromRight {
          0% {
            transform: translateX(100%);
            opacity: 0;
          }
          100% {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes glow {
          0% {
            box-shadow: 0 0 5px rgba(107, 33, 168, 0.5);
          }
          50% {
            box-shadow: 0 0 15px rgba(107, 33, 168, 0.8);
          }
          100% {
            box-shadow: 0 0 5px rgba(107, 33, 168, 0.5);
          }
        }
        .custom-table tbody tr td:nth-child(2) {
          text-align: left !important;
          font-family: 'Roboto', sans-serif !important;
        }
        .custom-table thead tr th:nth-child(2) {
          text-align: center !important;
          font-family: 'Roboto', sans-serif !important;
        }
        .pagination-button:hover:not(:disabled) {
          background: linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF);
          transform: scale(1.05);
          animation: glow 1.5s ease-in-out infinite;
        }
        .pagination-button:active:not(:disabled) {
          transform: scale(0.95);
        }
        .custom-dropdown {
          position: relative;
          width: 60px;
          padding: 5px 25px 5px 10px;
          border: 1px solid #ccc;
          border-radius: 5px;
          background-color: #fff;
          color: #1F2937;
          cursor: 'pointer';
          font-family: 'Roboto', sans-serif !important;
          backgroundImage: url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%236B21A8%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000%20.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2010-1.414-1.414z%22%2F%3E%3C%2Fsvg%3E");
          backgroundRepeat: no-repeat;
          backgroundPosition: right 10px center;
          backgroundSize: 12px;
        }

        .custom-dropdown-options {
          position: absolute;
          bottom: 100%;
          left: 0;
          width: 60px;
          border: 1px solid #ccc;
          border-radius: 5px;
          background-color: #fff;
          z-index: 1000;
          max-height: 200px;
          overflow-y: auto;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .custom-option {
          padding: 5px 10px;
          cursor: pointer;
        }

        .custom-option:hover {
          background: #D3D3D3 !important;
          color: #1F2937 !important;
        }

        .custom-dropdown-options::-webkit-scrollbar {
          width: 8px;
        }
        .custom-dropdown-options::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .custom-dropdown-options::-webkit-scrollbar-thumb {
          background: #8f8f8f;
          border-radius: 4px;
        }
        .custom-dropdown-options::-webkit-scrollbar-thumb:hover {
          background: #6B21A8;
        }

        .pagination-button:disabled {
          background-color: #ccc !important;
          color: #aaa !important;
          cursor: not-allowed;
        }

        .retry-button:hover {
          background-color: #6B21A8 !important;
          color: white !important;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(107, 33, 168, 0.3);
        }
      `}</style>
      <div style={{ ...styles.sidebar, position: 'fixed', top: 0, left: 0, zIndex: 10, transition: 'width 0.3s ease' }}>
        <AdminSidebar />
      </div>

      <div style={{ marginLeft: sidebarWidth, width: `calc(100% - ${sidebarWidth})`, transition: 'all 0.3s ease', fontFamily: "'Roboto', sans-serif" }}>
        <AdminNavbar />
        <div style={styles.content}>
          <div style={styles.headerContainer}>
            <div style={styles.searchFilterGroup}>
              <input
                type="text"
                placeholder="Search by student name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={styles.searchInput}
                className="search-input"
                aria-label="Search students"
              />
            </div>
          </div>
          
          {loading ? (
            <div style={styles.loadingContainer}>
              <p>Loading...</p>
            </div>
          ) : error ? (
            <div style={styles.errorContainer}>
              <p style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>{error}</p>
              <button
                onClick={handleRetry}
                style={styles.retryButton}
                className="retry-button"
              >
                {isTokenExpired ? 'Login Again' : 'Try Again'}
              </button>
            </div>
          ) : filteredStudents.length === 0 ? (
            <div style={styles.noDataContainer}>
              <p>No student results available.</p>
            </div>
          ) : (
            <div style={styles.tableContainer} className="table-container" ref={tableContainerRef}>
              <table className="custom-table" style={styles.table}>
                <thead style={styles.headerRow}>
                  <tr>
                    <th style={{ ...styles.th, width: '5%' }}>Sr.</th>
                    <th style={{ ...styles.th, width: '10%' }}>Student Name</th>
                    <th style={{ ...styles.th, width: '20%' }}>Test Name</th>
                    <th style={{ ...styles.th, width: '5%' }}>Score (%)</th>
                    <th style={{ ...styles.th, width: '10%' }}>Grade</th>
                    <th style={{ ...styles.th, width: '10%' }}>Date Attempted</th>
                    <th style={{ ...styles.th, width: '5%' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentStudents.map((s, index) => (
                    <tr
                      key={s.id}
                      style={styles.row}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(to right, #F0F5FF, #F5EDFF, #FFF0F5)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = (indexOfFirstStudent + index) % 2 === 0 ? '#FFFFFF' : 'linear-gradient(to right, #FAFCFF, #FBF6FF, #FFF8FB)';
                      }}
                    >
                      <td style={styles.td}>{indexOfFirstStudent + index + 1}</td>
                      <td style={styles.td}>{s.studentName}</td>
                      <td style={styles.td}>{s.testNumber}</td>
                      <td style={styles.td}>{s.score}%</td>
                      <td style={styles.td}>{s.grade}</td>
                      <td style={styles.td}>
                        {new Date(s.attemptedAt).toLocaleDateString("en-GB", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </td>
                      <td style={styles.td}>
                        <button
                          className="action-button"
                          style={styles.actionButton}
                          onClick={() => handleViewResult(s.id, s.studentName)}
                          title="View test result"
                          aria-label="View test result"
                        >
                          <VisibilityIcon style={{ ...styles.actionButton, fontFamily: "'Roboto', sans-serif" }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={styles.paginationContainer}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: "'Roboto', sans-serif" }}>
                  <span style={{ fontSize: '14px', color: '#1F2937', fontFamily: "'Roboto', sans-serif" }}>
                    Showing {indexOfFirstStudent + 1} to {Math.min(indexOfLastStudent, filteredStudents.length)} of {filteredStudents.length} entries
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: "'Roboto', sans-serif" }}>
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      ...styles.pageButtonPrevious,
                      backgroundColor: currentPage === 1 ? '#ccc' : undefined,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    }}
                    className="pagination-button"
                    aria-label="Previous page"
                  >
                    Previous
                  </button>
                  <div style={styles.paginationWrapper}>
                    {Array.from({ length: totalPages }, (_, i) => {
                      const pageNum = i + 1;
                      const shouldShow = 
                        pageNum === 1 || 
                        pageNum === totalPages || 
                        (pageNum >= currentPage - 2 && pageNum <= currentPage + 2);
                      
                      if (!shouldShow) {
                        if (
                          (pageNum === currentPage - 3 && currentPage > 4) || 
                          (pageNum === currentPage + 3 && currentPage < totalPages - 3)
                        ) {
                          return (
                            <span key={`ellipsis-${pageNum}`} style={{ padding: '0 5px', color: '#6B21A8', fontFamily: "'Roboto', sans-serif" }}>
                              ...
                            </span>
                          );
                        }
                        return null;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => paginate(pageNum)}
                          style={{
                            ...styles.pageNumberButton,
                            background: currentPage === pageNum ? 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)' : '#ffffff',
                            color: currentPage === pageNum ? '#6B21A8' : '#333',
                          }}
                          className={`pagination-button page-number-button ${currentPage === pageNum ? 'active' : ''}`}
                          aria-label={`Page ${pageNum}`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      ...styles.pageButton,
                      backgroundColor: currentPage === totalPages ? '#ccc' : undefined,
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    }}
                    className="pagination-button"
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: "'Roboto', sans-serif", position: 'relative' }}>
                  <div
                    className="custom-dropdown"
                    style={styles.customDropdown}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    {currentPage}
                    {isDropdownOpen && (
                      <div className="custom-dropdown-options" style={styles.customDropdownOptions}>
                        {pageRange.map((pageNum) => (
                          <div
                            key={pageNum}
                            className="custom-option"
                            style={styles.customOption}
                            onClick={() => {
                              paginate(pageNum);
                              setIsDropdownOpen(false);
                            }}
                          >
                            {pageNum}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}